import * as React from 'react';
import { textFieldDriverFactory } from './TextField.legacy.driver';
import { TextField, TextFieldLabelStyle, TextFieldTheme } from '.';
import { createRendererWithDriver } from '../../test/utils';
import { Calendar } from '@wix/wix-ui-icons-common/on-stage';
import eventually from 'wix-eventually';
import { Simulate } from 'react-dom/test-utils';
import { DATA_HOOKS } from './dataKeys';
import { act } from '@testing-library/react';

describe('TextField', () => {
  const createDriver = createRendererWithDriver(textFieldDriverFactory);

  it('should render', () => {
    const value = 'hello!';
    const { driver } = createDriver(<TextField value={value} />);
    expect(driver.getValue()).toEqual(value);
  });

  describe('TextFieldTestKit', () => {
    const ref: React.RefObject<TextField> = React.createRef();

    it('should getTheme', () => {
      const { driver: driverLine } = createDriver(
        <TextField value="Theme Test" theme={TextFieldTheme.Line} />,
      );
      const { driver: driverBox } = createDriver(
        <TextField value="Theme Test" theme={TextFieldTheme.Box} />,
      );
      expect(driverLine.getTheme()).toBe(TextFieldTheme.Line);
      expect(driverBox.getTheme()).toBe(TextFieldTheme.Box);
    });

    it('should set lang to root', async () => {
      const lang = 'he';
      const { driver } = createDriver(<TextField lang={lang} />);

      expect(await driver.getLang()).toBe(lang);
    });

    it('should isSuccess', () => {
      const { driver } = createDriver(<TextField success value="Theme Test" />);
      expect(driver.isSuccess()).toBe(true);
    });

    it('should isSuccess with success icon', () => {
      const { driver } = createDriver(
        <TextField success successIcon value="Theme Test" />,
      );
      expect(driver.isSuccess()).toEqual(true);
      expect(driver.isSuccessIconExist()).toEqual(true);
    });

    it('should trigger focus', async () => {
      const { driver } = createDriver(
        <TextField ref={ref} success successIcon value="Theme Test" />,
      );

      expect(driver.isFocused()).toEqual(false);
      await act(async () => ref.current.focus());
      expect(driver.isFocused()).toEqual(true);
    });

    it('should trigger blur', async () => {
      const { driver } = createDriver(
        <TextField ref={ref} success successIcon value="Theme Test" />,
      );

      await act(async () => ref.current.focus());
      expect(driver.isFocused()).toEqual(true);
      await act(async () => ref.current.blur());
      expect(driver.isFocused()).toEqual(false);
    });

    it('should getErrorMessage', async () => {
      const { driver } = createDriver(
        <TextField error errorMessage="ErrorMessage" value="Theme Test" />,
      );

      expect(driver.isError()).toBe(true);
      expect(driver.getErrorMessage()).toBe('ErrorMessage');
    });

    it('should hasEmptyState', () => {
      const { driver: emptyStateDriver } = createDriver(
        <TextField error errorMessage="ErrorMessage" value="" />,
      );
      expect(emptyStateDriver.hasEmptyState()).toBe(true);
      const { driver: notEmptyStateDriver } = createDriver(
        <TextField error errorMessage="ErrorMessage" value="Some data" />,
      );
      expect(notEmptyStateDriver.hasEmptyState()).toBe(false);
    });

    it('should have label', () => {
      const { driver } = createDriver(<TextField label="label" />);
      expect(driver.isLabelExists()).toBe(true);
      expect(driver.getLabelText()).toBe('label');
    });

    it('should have label for id attribute', async () => {
      const { driver } = createDriver(<TextField label="label" />);
      expect(driver.isLabelExists()).toBe(true);
      expect(driver.getLabelText()).toBe('label');

      const labelForInput = await driver.getLabelForInput();
      const dropdownId = await driver.getInputId();

      expect(labelForInput).toEqual(dropdownId);
    });

    it('should have For attribute same as recieved ID prop', async () => {
      const { driver } = createDriver(
        <TextField id="some-id" label="some-label" />,
      );

      const labelForInput = await driver.getLabelForInput();
      const inputId = await driver.getInputId();

      expect(labelForInput).toEqual(inputId);
    });

    it('should have a label placeholder when forcing label', () => {
      const { driver } = createDriver(<TextField forceLabel />);
      expect(driver.isLabelPlaceHolderExists()).toBe(true);
    });

    it('should not have label', () => {
      const { driver } = createDriver(<TextField />);
      expect(driver.isLabelExists()).toBe(false);
    });

    it('should onChange', () => {
      const onChange = jest.fn();
      const { driver } = createDriver(
        <TextField onChange={onChange} value="Test" />,
      );
      driver.setValue('Test 1');
      expect(onChange.mock.calls[0][0].target.value).toBe('Test 1');
    });

    it('should be readonly when readOnlyCustom prop is passed', () => {
      const { driver } = createDriver(<TextField readOnlyCustom />);
      expect(driver.isReadOnly()).toBe(true);
    });

    it('should be readonly when readOnly prop is passed', () => {
      const { driver } = createDriver(<TextField readOnly />);
      expect(driver.isReadOnly()).toBe(true);
    });

    it('should disabled work', async () => {
      const onChange = jest.fn();
      const { driver: disabledStateDriver } = createDriver(
        <TextField disabled onChange={onChange} value="Test" />,
      );
      const { driver: regularStateDriver } = createDriver(
        <TextField onChange={onChange} value="Test" />,
      );
      await eventually(() => {
        expect(disabledStateDriver.isDisabled()).toBe(true);
        expect(regularStateDriver.isDisabled()).toBe(false);
      });
    });

    it('should pass required prop to input element', () => {
      const { driver } = createDriver(<TextField required />);
      expect(driver.getInput().required).toBe(true);
    });

    it('should has clear button', () => {
      const { driver: emptyStateDriver } = createDriver(
        <TextField withClearButton value="text" />,
      );
      expect(emptyStateDriver.hasClearButton()).toBe(true);
    });

    it('should not have clear button when the input is empty', () => {
      const { driver: emptyStateDriver } = createDriver(
        <TextField withClearButton value="" />,
      );
      expect(emptyStateDriver.hasClearButton()).toBe(false);
    });

    it('should call onClear after clicking on the clear button', () => {
      const onClear = jest.fn();
      const { driver } = createDriver(
        <TextField withClearButton onClear={onClear} value="text" />,
      );
      driver.clickOnClearButton();
      expect(onClear).toHaveBeenCalled();
    });

    it('should has custom suffix', () => {
      const { driver: emptyStateDriver } = createDriver(
        <TextField value="hello" suffix={<Calendar />} />,
      );
      expect(emptyStateDriver.hasCustomSuffix()).toBe(true);
    });

    it('should not have custom suffix', () => {
      const { driver: emptyStateDriver } = createDriver(
        <TextField value="hello" />,
      );
      expect(emptyStateDriver.hasCustomSuffix()).toBe(false);
    });

    it('should have maxLength attribute', () => {
      const maxLength = 2;
      const { driver } = createDriver(<TextField maxLength={maxLength} />);

      expect(driver.getMaxLength()).toEqual(maxLength);
    });

    it('should show charCount as numOfChars/maxLength', async () => {
      const maxLength = 5;
      const value = 'abc';

      const { driver } = createDriver(
        <TextField maxLength={maxLength} showCharCount value={value} />,
      );
      expect(driver.isCharCountExist()).toBe(true);
      expect(await driver.getCharCountText()).toBe(
        `${value.length}/${maxLength}`,
      );
    });

    it('should not show charCount if maxLength is not passed', async () => {
      const { driver } = createDriver(<TextField showCharCount />);
      expect(driver.isCharCountExist()).toBe(false);
    });

    it('should have a prefix', () => {
      const somePrefix = '$';
      const { driver } = createDriver(
        <TextField value="text" prefix={somePrefix} />,
      );
      const prefix = driver.getPrefix();

      expect(prefix).not.toBeNull();
      expect(prefix.textContent).toBe(somePrefix);
    });

    it('should not have a prefix', () => {
      const { driver } = createDriver(<TextField value="text" />);
      expect(driver.getPrefix()).toBeNull();
    });

    it('should have a prefix label', () => {
      const somePrefix = '$';
      const somePrefixLabel = 'USD';
      const { driver } = createDriver(
        <TextField
          value="text"
          prefix={somePrefix}
          prefixLabel={somePrefixLabel}
        />,
      );
      const prefixLabel = driver.getPrefixLabel();

      expect(prefixLabel).not.toBeNull();
      expect(prefixLabel.textContent).toBe(somePrefixLabel);
    });

    it('should not have a prefix label', () => {
      const { driver } = createDriver(<TextField value="text" />);
      expect(driver.getPrefixLabel()).toBeNull();
    });

    it('should have prefix label id in aria-describedby if the prefix label exists', () => {
      const somePrefix = '$';
      const somePrefixLabel = 'USD';
      const { driver } = createDriver(
        <TextField
          value="text"
          prefix={somePrefix}
          prefixLabel={somePrefixLabel}
        />,
      );
      const inputElement = driver.getInput();
      const ariaDescribedBy = inputElement.getAttribute('aria-describedby');
      const prefixLabel = driver.element().querySelector(`#${ariaDescribedBy}`);

      expect(prefixLabel.textContent).toEqual(somePrefixLabel);
    });

    it('should not have arrows', () => {
      const { driver } = createDriver(<TextField value="hello" />);
      expect(driver.isArrowIconsExist()).toBe(false);
    });

    it('should have arrows when type is number', () => {
      const { driver } = createDriver(<TextField value="10" type="number" />);
      expect(driver.isArrowIconsExist()).toBe(true);
    });

    describe('arrow icons', () => {
      it('should have tabindex attribute -1', () => {
        const { driver } = createDriver(<TextField value="10" type="number" />);
        expect(driver.getUpArrowIconTabIndex()).toBe('-1');
        expect(driver.getDownArrowIconTabIndex()).toBe('-1');
      });
    });

    it('should call inputRef', () => {
      const inputRef = jest.fn();
      createDriver(<TextField inputRef={inputRef} />);
      expect(inputRef).toHaveBeenCalled();
    });

    it('should show helper text', () => {
      const { driver } = createDriver(
        <TextField helperText="Helpful information." />,
      );
      expect(driver.getHelperText()).toEqual('Helpful information.');
    });

    it('should trigger onPasswordVisibilityChange', () => {
      const onPasswordVisibilityChange = jest.fn();
      const { driver } = createDriver(
        <TextField
          type="password"
          onPasswordVisibilityChange={onPasswordVisibilityChange}
        />,
      );
      driver.clickOnPasswordVisibilityButton();
      expect(onPasswordVisibilityChange).toHaveBeenLastCalledWith('hidden');
      driver.clickOnPasswordVisibilityButton();
      expect(onPasswordVisibilityChange).toHaveBeenLastCalledWith('visible');
    });

    it('should show and hide password', () => {
      const { driver } = createDriver(<TextField type="password" />);
      driver.clickOnPasswordVisibilityButton();
      expect(driver.getInput().type).toBe('text');
      driver.clickOnPasswordVisibilityButton();
      expect(driver.getInput().type).toBe('password');
    });

    it('should show and hide password with keyboard', () => {
      const { driver } = createDriver(<TextField type="password" />);
      Simulate.keyDown(
        driver
          .element()
          .querySelector(`[data-hook="${DATA_HOOKS.VISIBLE_ICON}"]`),
        {
          key: 'Enter',
        },
      );
      expect(driver.getInput().type).toBe('text');
      Simulate.keyDown(
        driver
          .element()
          .querySelector(`[data-hook="${DATA_HOOKS.HIDDEN_ICON}"]`),
        {
          key: 'Enter',
        },
      );
      expect(driver.getInput().type).toBe('password');
    });

    it('should show password from the start', () => {
      const { driver } = createDriver(<TextField type="password" visible />);
      expect(driver.getInput().type).toBe('text');
    });

    describe('error tooltip', () => {
      const someErrorMessage = 'someErrorMessage';

      it('should toggle error tooltip when clicking on error icon', async () => {
        const { driver } = createDriver(
          <TextField error errorMessage={someErrorMessage} />,
        );
        expect(driver.isErrorTooltipShown()).toBeFalsy();

        driver.clickOnErrorIcon();

        await eventually(async () => {
          expect(driver.isErrorTooltipShown()).toBeTruthy();
        });

        driver.clickOnErrorIcon();

        await eventually(async () => {
          expect(driver.isErrorTooltipShown()).toBeFalsy();
        });
      });

      it('should set the correct a11y attributes', async () => {
        const { driver } = createDriver(
          <TextField error errorMessage={someErrorMessage} />,
        );

        driver.clickOnErrorIcon();

        await eventually(async () => {
          expect(driver.isErrorTooltipShown()).toBeTruthy();
        });

        const inputElement = driver.getInput();
        const ariaDescribedBy = inputElement.getAttribute('aria-describedby');

        expect(ariaDescribedBy).toBeDefined();

        const errorMessage = document.querySelector(`#${ariaDescribedBy}`);

        expect(errorMessage).not.toBeNull();
        expect(errorMessage.textContent).toBe(someErrorMessage);
      });

      it('aria describedby should be concatenated to value from props', async () => {
        const { driver } = createDriver(
          <TextField
            error
            errorMessage={someErrorMessage}
            aria-describedby="some descriptions"
          />,
        );

        driver.clickOnErrorIcon();

        await eventually(async () => {
          expect(driver.isErrorTooltipShown()).toBeTruthy();
        });

        expect(driver.getErrorMessage()).toBe(someErrorMessage);
      });
    });

    it('should have floating label style', () => {
      const { driver } = createDriver(
        <TextField
          labelStyle={TextFieldLabelStyle.Floating}
          label="floating label"
        />,
      );
      expect(driver.isFloatingLabelExists()).toBe(true);
      expect(driver.getFloatingLabelText()).toBe('floating label');
    });
  });
});
